import { BallFinalMetricsConfigurator, BfMetricTileSize, IBfMetricListItem, UnitSystem } from '@trackman/baseball-shared-components';
import { ABSModal, ChallengeCall, ChallengeResult, ChallengeTeam } from 'components/ABSChallenge/components/ABSModal';
import EndGameModal from 'components/header/NavBar/GameControl/components/EndGameModal/EndGameModal';
import { ServiceTypes } from 'constants/ServiceTypes';
import { BroadcastItems } from 'enums/BroadcastItems';
import { SessionMode } from 'enums/SessionMode';
import { History } from 'history';
import { StrikeZoneLocation } from 'interfaces/session/IStrikeZoneDefinition';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { BroadcastConsumerService } from 'services/data-services/BroadcastConsumerService';
import { useInjection } from 'services/ioc/ioc.react';
import { NotificationStore } from 'stores/NotificationStore';
import { SessionStore } from 'stores/SessionStore';
import { TaggingStore } from 'stores/Tagging/TaggingStore';
import { IStrikeZoneDecisionModel } from '../../interfaces/tagging/ApiModels/IStrikeZoneDecision';
import { LogService } from '../../services/data-services/LogService';
import { MetricStore } from '../../stores/MetricStore';
import AdvanceBatterModal from './components/AdvanceBatterModal/AdvanceBatterModal';
import InningChangeModal from './components/InningChangeModal/InningChangeModal';
import LineupList from './components/Lineup/LineupList/LineupList';
import { Metrics, StrikeZoneDimension } from './components/Metrics/Metrics';
import PlayList from './components/PlayList/PlayList';
import { TaggingFlow } from './components/TaggingFlow/TaggingFlow';
import { HeightWrapper, TaggingPageWrapper } from './TaggingStyles';

interface Props {
  history: History;
}

const Tagging: React.FC<Props> = observer((props) => {
  const { history } = props;
  const taggingStore = useInjection<TaggingStore>(ServiceTypes.TaggingStore);
  const metricStore = useInjection<MetricStore>(ServiceTypes.MetricStore);
  const logService = useInjection<LogService>(ServiceTypes.LogService);
  const { playStore } = taggingStore;
  const sessionStore = useInjection<SessionStore>(ServiceTypes.SessionStore);
  const broadcastService = useInjection<BroadcastConsumerService>(ServiceTypes.BroadcastConsumerService);
  const [isSubscribedToSessionModeChanges, setIsSubscribedToSessionModeChanges] = useState(true);
  const [challengingTeam, setChallengingTeam] = useState<ChallengeTeam | null>(null);
  const [umpireCall, setUmpireCall] = useState<ChallengeCall | null>(null);
  const [challengeOutcome, setChallengeOutcome] = useState<ChallengeResult | null>(null);
  const notificationStore = useInjection<NotificationStore>(ServiceTypes.NotificationStore);

  const initializeStores = async (): Promise<void> => {
    if (!taggingStore.isInitialized) {
      await taggingStore.resumeSession();
    }
  };

  const isSessionEnded = (): boolean => {
    return isSubscribedToSessionModeChanges && sessionStore.currentSessionMode.sessionMode === SessionMode.SessionEnded;
  };

  const onBallFinal = (ballFinal: any): void => {
    logService.logInfo(`Received BallFinal message. PlayId: ${ballFinal?.ballFinal?.playId}`);
    playStore.createOrUpdatePlayFromBroadcast(ballFinal.ballFinal, BroadcastItems.BallFinal);
  };

  const onPitchLive = (pitchLive: any): void => {
    logService.logInfo(`Received PitchLive message. PlayId: ${pitchLive?.pitchLive?.playId}`);
    playStore.createOrUpdatePlayFromBroadcast(pitchLive.pitchLive, BroadcastItems.PitchLive);
  };

  const onStrikeZoneDecision = (strikeZoneDecision: IStrikeZoneDecisionModel): void => {
    playStore.handleStrikeZoneDecision(strikeZoneDecision);
  };

  const handleOnEndGameCallback = async (): Promise<void> => {
    setIsSubscribedToSessionModeChanges(false);

    const isSuccess = await sessionStore.setCurrentSessionMode({
      ...sessionStore.currentSessionMode,
      sessionMode: SessionMode.SessionEnded,
    });
    sessionStore.setIsEndGameModalVisible(false);
    if (isSuccess) {
      history.push(`/review/${sessionStore.currentSessionMode.sessionId}`);
    } else {
      notificationStore.addNotification({
        id: 'error-changing-session-mode',
        message: 'Error changing session mode',
        variant: 'error',
        removable: true,
        onClose: () => {
          notificationStore.removeNotification('error-changing-session-mode');
        },
      });
    }
  };

  const handleOnInningChangeDismissCallback = (): void => {
    playStore.setIncreaseInningRequest(false);
    playStore.setDecreaseInningRequest(false);
  };

  useEffect(() => {
    if (!!sessionStore.currentSessionMetadata.isOperatorlessTaggingEnabled) {
      history.push('/');
      return;
    }

    initializeStores();
    taggingStore.setIsTagging(true);
    broadcastService.subscribeBallEvents(onBallFinal, onPitchLive);
    broadcastService.subscribeStrikeZoneDecision(onStrikeZoneDecision);

    return (): void => {
      taggingStore.setIsTagging(false);
      taggingStore.cleanUp();
      broadcastService.unsubscribeBallEvents();
      broadcastService.unsubscribeStrikeZoneDecision();
    };
  }, []);

  useEffect(() => {
    if (isSessionEnded()) {
      notificationStore.addNotification({
        id: 'session-ended',
        message: 'Session was ended while you were tagging',
        variant: 'error',
        removable: true,
        onClose: () => {
          notificationStore.removeNotification('session-ended');
        },
      });
      history.push('/');
    }
  }, [sessionStore.currentSessionMode]);

  useEffect(() => {
    if (playStore.isChallengeModalVisible) {
      const tag = playStore.challengeTargetPlay?.challengeTag;
      setChallengingTeam(tag?.challengeTeam ?? null);
      setUmpireCall(tag?.challengeCall ?? null);
      setChallengeOutcome(tag?.challengeResult ?? null);
    }
  }, [playStore.isChallengeModalVisible]);

  const formatInning = (inningNumber: number, isTop: boolean): string => {
    const mod100 = inningNumber % 100;
    const mod10 = inningNumber % 10;
    let suffix = 'th';
    if (mod100 < 11 || mod100 > 13) {
      if (mod10 === 1) suffix = 'st';
      else if (mod10 === 2) suffix = 'nd';
      else if (mod10 === 3) suffix = 'rd';
    }
    return `${isTop ? 'Top' : 'Bot'} ${inningNumber}${suffix}`;
  };

  const isLineupListDisabled = () => {
    return !playStore.inningManager.isFirstPlayInInning() && playStore.isPlaceholderPlaySelected;
  };

  const getLastSZDecision = (): StrikeZoneDimension | undefined => {
    const lastLivePlay = playStore.inningManager.getLastPlay(true);

    const priorityOrder = [StrikeZoneLocation.Front, StrikeZoneLocation.Middle, StrikeZoneLocation.Back];

    const data = lastLivePlay?.strikeZoneDecision?.strikeZoneDecisionLocations.find((location) =>
      priorityOrder.includes(location.strikeZoneLocationType)
    );

    return data ? data : lastLivePlay?.strikeZoneDecision;
  };

  return taggingStore.isInitialized ? (
    <>
      <TaggingPageWrapper>
        <div className='row'>
          <div className='col-2'>
            <HeightWrapper>
              <PlayList />
            </HeightWrapper>
          </div>
          <div className='col-3'>
            <HeightWrapper>
              <LineupList disabled={isLineupListDisabled()} />
            </HeightWrapper>
          </div>
          <div className='col-5'>
            <HeightWrapper>
              <TaggingFlow review={false} selectedPlay={playStore.selectedPlay} disabled={playStore.isPlaceholderPlaySelected} />
            </HeightWrapper>
          </div>
          <div className='col-2'>
            <HeightWrapper>
              <Metrics
                ballFinals={playStore.selectedPlay?.ballFinals ?? undefined}
                strikeZoneDecision={playStore.selectedPlay?.strikeZoneDecision ?? undefined}
                connected={sessionStore.isDataBroadcasterConnected}
                previousStrikeZoneDecision={getLastSZDecision()}
                isPlayPlaceholder={playStore.isPlaceholderPlaySelected}
                strikeZoneType={sessionStore.currentSessionMetadata.strikeZone.activeStrikeZone}
              />
            </HeightWrapper>
          </div>
          <BallFinalMetricsConfigurator
            metrics={metricStore.filteredMetrics}
            tileSize={metricStore.tileSize}
            unitSystem={metricStore.unitSystem}
            onConfigChanged={(metrics: IBfMetricListItem[], unitSystem: UnitSystem, tileSize: BfMetricTileSize) => {
              metricStore.setMetrics(metrics);
              metricStore.setTileSize(tileSize);
              metricStore.setUnitSystem(unitSystem);
            }}
            onHide={() => metricStore.setIsSettingsMenuVisible(false)}
            isVisible={metricStore.isSettingsMenuVisible}
            width='300px'
          />
        </div>
      </TaggingPageWrapper>
      {sessionStore.isEndGameModalVisible && (
        <EndGameModal
          dismissCallback={(): void => sessionStore.setIsEndGameModalVisible(false)}
          endGameCallback={handleOnEndGameCallback}
        />
      )}
      {(playStore.increaseInningRequest || playStore.decraseInningRequest) && (
        <InningChangeModal onDismissCallback={handleOnInningChangeDismissCallback} />
      )}
      {playStore.isAdvanceBatterModalVisible && (
        <AdvanceBatterModal
          onDismissCallback={() => {
            playStore.isAdvanceBatterModalVisible = false;
          }}
        />
      )}
      <ABSModal
        open={playStore.isChallengeModalVisible}
        isChallenge={!!playStore.challengeTargetPlay?.challengeTag}
        onClose={() => playStore.closeChallengeModal()}
        pitchNumber={playStore.challengeTargetPlay?.playSequenceNumber}
        inning={formatInning(playStore.challengeTargetPlay?.inningNumber ?? 1, playStore.challengeTargetPlay?.isTopInning ?? true)}
        homeTeamName={sessionStore.currentSessionMetadata.homeTeam?.name ?? ''}
        awayTeamName={sessionStore.currentSessionMetadata.awayTeam?.name ?? ''}
        challengingTeam={challengingTeam}
        umpireCall={umpireCall}
        challengeOutcome={challengeOutcome}
        onChallengeTeamChange={setChallengingTeam}
        onUmpireCallChange={setUmpireCall}
        onOutcomeChange={setChallengeOutcome}
        onRemoveFromPlay={() => playStore.removeChallengeTag()}
        onApplyToPlay={() => {
          if (challengingTeam && umpireCall && challengeOutcome) {
            playStore.setChallengeTag(challengingTeam, umpireCall, challengeOutcome);
          }
        }}
      />
    </>
  ) : null;
});

export default Tagging;
